import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { KTIcon } from '../../_metronic/helpers'
import { createCashBox } from '../../app/common/core/_requests'
import { CreateCashBoxRequest, ICashBox } from '../../types'
import { getErrorResponse, getErrorStatus, getErrorText, FORBIDDEN_TEXT, SERVICE_UNAVAILABLE_TEXT } from '../../app/common/api/errors'

const AppSwal = withReactContent(Swal)

interface CreateCashBoxProps {
  show: boolean
  onHide: () => void
  onCashBoxCreated?: (newCashBox: ICashBox) => void
  existingCashBoxes: ICashBox[]
}

const createCashBoxSchema = (existingTitles: string[]) => Yup.object().shape({
  title: Yup.string()
    .required('Введите название кассы')
    .max(30, 'Максимальная длина 30 символов')
    .test('unique', 'Касса с таким названием уже существует', function(value) {
      if (!value) return true
      return !existingTitles.some((title: string) => 
        title.toLowerCase() === value.toLowerCase()
      )
    }),
  callbackUrl: Yup.string()
    .url('Введите корректный URL')
    .nullable()
})

/** Текст отказа создания кассы: 403 и 503 — фиксированные тексты, иначе причина бэкенда (дубль названия — 409). */
export const cashBoxCreateErrorText = (error: unknown): string => {
  const status = getErrorStatus(error)
  if (status === 503) {
    return SERVICE_UNAVAILABLE_TEXT
  }
  if (status === 403) {
    return getErrorText(error, FORBIDDEN_TEXT)
  }

  const errors = getErrorResponse<unknown>(error)
  const errorValues =
    status !== undefined && status < 500 && errors && typeof errors === 'object' && !Array.isArray(errors)
      ? Object.values(errors as Record<string, unknown>).filter((v): v is string => typeof v === 'string' && v !== '')
      : []

  return errorValues.length > 0
    ? errorValues.join('\n')
    : `Произошла ошибка при создании кассы${status ? ` (${status})` : ''}`
}

export const CreateCashBox: React.FC<CreateCashBoxProps> = ({ 
  show, 
  onHide, 
  onCashBoxCreated, 
  existingCashBoxes 
}) => {
  const [loading, setLoading] = useState(false)

  const existingTitles = existingCashBoxes.map(cb => cb.title)

  const formik = useFormik<CreateCashBoxRequest>({
    initialValues: {
      title: '',
      callbackUrl: ''
    },
    validate: async (values) => {
      try {
        await createCashBoxSchema(existingTitles).validate(values, { abortEarly: false })
        return {}
      } catch (err: any) {
        const errors: any = {}
        if (err.inner) {
          err.inner.forEach((error: any) => {
            errors[error.path] = error.message
          })
        }
        return errors
      }
    },
    onSubmit: (values) => {
      // Убираем пустой callbackUrl
      const submitData: CreateCashBoxRequest = {
        title: values.title,
        ...(values.callbackUrl && { callbackUrl: values.callbackUrl })
      }
      handleCreateCashBox(submitData)
    },
    enableReinitialize: true
  })

  const handleCreateCashBox = async (data: CreateCashBoxRequest) => {
    setLoading(true)
    
    try {
      const response = await createCashBox(data)
      
      if (response.data?.success && response.data.data) {
        formik.resetForm()
        
        AppSwal.fire({
          title: 'Касса успешно создана',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        }).then(() => {
          onHide()
          onCashBoxCreated?.(response.data.data!)
        })
      } else if (response.data?.success === false) {
        // Обрабатываем случай, когда API вернул ошибку в response
        const responseData = response.data
        
        if (responseData?.errors && typeof responseData.errors === 'object') {
          // Извлекаем все значения ошибок
          const errorValues = Object.values(responseData.errors).filter(Boolean)
          
          if (errorValues.length > 0) {
            // Показываем все найденные ошибки
            const errorMessage = errorValues.join('\n')
            Swal.fire({ title: 'Ошибка', text: errorMessage, icon: 'error' })
          } else {
            Swal.fire({ title: 'Ошибка', text: 'Произошла ошибка при создании кассы', icon: 'error' })
          }
        } else {
          Swal.fire({ title: 'Ошибка', text: 'Произошла ошибка при создании кассы', icon: 'error' })
        }
      }
    } catch (error: unknown) {
      console.error('Ошибка создания кассы:', error)

      Swal.fire({ title: 'Ошибка', text: cashBoxCreateErrorText(error), icon: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      formik.resetForm()
      onHide()
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      size="lg"
      centered
    >
      <div className="modal-header">
        <h2 className="modal-title">Создать кассу</h2>
        {!loading && (
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={handleClose}
          >
            <KTIcon className="fs-1" iconName="cross" />
          </div>
        )}
      </div>

      <div className="modal-body py-lg-6 px-lg-6">
        <form onSubmit={formik.handleSubmit} noValidate className="form">
          <div className="row mb-6">
            <label className="col-lg-4 col-form-label required fw-bold fs-6">
              Название
            </label>
            <div className="col-lg-8 fv-row">
              <input
                type="text"
                className="form-control form-control-lg form-control-solid"
                placeholder="Введите название кассы"
                maxLength={30}
                disabled={loading}
                {...formik.getFieldProps('title')}
              />
              {formik.touched.title && formik.errors.title && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.title}</div>
                </div>
              )}
            </div>
          </div>

          <div className="row mb-6">
            <label className="col-lg-4 col-form-label fw-bold fs-6">
              URL (необязательно)
            </label>
            <div className="col-lg-8 fv-row">
              <input
                type="url"
                className="form-control form-control-lg form-control-solid"
                placeholder="https://example.com/"
                disabled={loading}
                {...formik.getFieldProps('callbackUrl')}
              />
              {formik.touched.callbackUrl && formik.errors.callbackUrl && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.callbackUrl}</div>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-light me-3"
              onClick={handleClose}
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading && <span className="spinner-border spinner-border-sm me-2" />}
              Создать
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
} 